import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import type { datadogLogs, LogsEvent } from '@datadog/browser-logs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatadogContextStateService } from './datadog-context-state.service';
import { LogEventSanitizerHelper } from './log-event-sanitizer.helper';

@Injectable({
  providedIn: 'root',
})
export class LoggerService implements OnDestroy {
  private _contextStateService: DatadogContextStateService = inject(DatadogContextStateService);
  private _datadogLogs: typeof datadogLogs | undefined;
  private _destroy$ = new Subject<void>();
  private _ngZone: NgZone = inject(NgZone);
  private _queuedLogs: Array<{ level: 'error' | 'info' | 'warn'; msg: string }> = [];
  private allowLogFlag = false;

  public constructor() {
    this._contextStateService.state$.pipe(takeUntil(this._destroy$)).subscribe((state) => {
      this._updateDatadogContext(state);
    });
  }

  public debug(msg: string): void {
    console.debug(msg);
  }

  public error(msg: string): void {
    if (this.allowLogFlag) {
      if (this._datadogLogs) {
        this._datadogLogs.logger.error(msg);
      } else {
        this._queuedLogs.push({ level: 'error', msg });
      }
    }
    console.error(msg);
  }

  public info(msg: string): void {
    if (this.allowLogFlag) {
      if (this._datadogLogs) {
        this._datadogLogs.logger.info(msg);
      } else {
        this._queuedLogs.push({ level: 'info', msg });
      }
    }
    console.info(msg);
  }

  public initialize(env: string): void {
    this.allowLogFlag = env === 'aws_production' || env === 'aws_demo';

    this._ngZone.runOutsideAngular(() => {
      if (this.allowLogFlag) {
        console.log(`Initializating Datadog`);
        import('@datadog/browser-logs').then((mod) => {
          this._datadogLogs = mod.datadogLogs;
          // definition of the clientToken key inside datadog :
          // https://app.datadoghq.eu/account/settings#api
          this._datadogLogs.init({
            clientToken: 'puba350e36b0584d9708f3fd2af792a7a74',
            site: 'datadoghq.eu',
            forwardErrorsToLogs: true,
            beforeSend: (event) => this._beforeSend(event),
          });

          const currentContext = this._datadogLogs.getGlobalContext();
          this._datadogLogs.setGlobalContext({ ...currentContext, env });
          this._flushQueue();
          console.log(`Datadog initialized`);
        });
      }
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public setCustomerCode(customerCode: string | null): void {
    this._contextStateService.setCustomerCode(customerCode);
  }

  public setLabCode(labCode: string | null): void {
    this._contextStateService.setLaboratoryCode(labCode);
  }

  public setUserUuid(userUuid: string | null): void {
    this._contextStateService.setUserUuid(userUuid);
  }

  public warn(msg: string): void {
    if (this.allowLogFlag) {
      if (this._datadogLogs) {
        this._datadogLogs.logger.warn(msg);
      } else {
        this._queuedLogs.push({ level: 'warn', msg });
      }
    }
    console.warn(msg);
  }

  // Strips query strings and fragments from every URL of the event (search text lives in `filter` / `pstate`
  // and must never reach Datadog), then downgrades known false positives.
  private _beforeSend(event: LogsEvent): boolean {
    LogEventSanitizerHelper.sanitize(event);
    return this._downgradeKnownFalsePositives(event);
  }

  // Downgrades known false positives from error to warn so they no longer trigger Datadog error alerts.
  // #8020: XHR/fetch with status 0 (request aborted by browser — navigation, tab close, network loss)
  // #8021: Dynamic import failure after deployment (old chunks no longer exist, page auto-reloads)
  // #7020 (broadened by #8424): OIDC discovery/jwks/userinfo loading with status 0 (same cause as
  //        #8020, but logged via console.error by angular-oauth2-oidc so event.http.status_code is
  //        not populated — match serialized payload). Covers the three lib messages "error loading
  //        discovery document", "error loading jwks" and "error loading user info". Scoped to status 0
  //        so the 5xx applicative outages (e.g. identity server down) stay at error level.
  // #8129a: JWT validation failures (expiry, signature, audience) on long-lived sessions or
  //         expired shared preview links — lib angular-oauth2-oidc emits "Token has expired" and
  //         "Error validating tokens" via console.error, non-actionable user-side noise
  // #8129b: OIDC state/nonce mismatch on multi-tab login or callback page reload — lib emits
  //         "wrong state/nonce" via console.error (sessionStorage desync, not a bug, expected
  //         behavior with OIDC code flow)
  // #8181:  companion log of "Token has expired" — angular-oauth2-oidc emits a second
  //         console.error with the raw { now, issuedAtMSec, expiresAtMSec } object right after
  //         the "Token has expired" string. The message has no readable text (just JSON), so
  //         the #8129a rule misses it — match on the serialized payload instead.
  // #7014: Zone.js internal state machine violation ("can not transition to ... expecting state")
  //        triggered when a third party monkey-patches setTimeout/Promise after zone.js loads
  //        (browser extension, corporate antivirus/EDR, injected proxy script). Stack is fully
  //        inside polyfills.*.js, no application code involved, not actionable on our side.
  // "Script error.": opaque cross-origin script failure (browser hides the real stack for
  //        security when a script without CORS headers throws). The only cross-origin script we
  //        load is Google Tag Manager, which injects third-party tags; a misbehaving tag can throw
  //        in a loop on a single session (notably Safari, where ITP blocks tag storage), flooding
  //        the logs. The message carries no actionable stack on our side, so downgrade it to warn.
  // #8424a: angular-oauth2-oidc logs "Error getting token" via console.error on the token endpoint.
  //         Status 0 = the XHR was aborted (navigation, tab close, flaky network) before a response,
  //         non-actionable. Scoped to status 0 so the 400/invalid_grant and 5xx variants (real auth
  //         or server problems) stay at error level.
  // #8424b: our runInitialLoginSequence catch (auth.service.ts) re-logs the underlying discovery/jwks/
  //         userinfo load failure. Status 0 = network abort during the init sequence, same transient
  //         cause as #8020/#7020. Scoped to status 0 so the -3 (auth required) and 5xx (server outage)
  //         variants stay at error level.
  // #8424c: angular-oauth2-oidc emits "Wrong nonce: <nonce>" (a distinct string from the "wrong
  //         state/nonce" of #8129b) when the stored nonce desyncs (multi-tab login, callback reload).
  //         Expected behavior with OIDC code flow, not a bug.
  // #8424d: "ResizeObserver loop completed with undelivered notifications." (and the older "ResizeObserver
  //         loop limit exceeded") is a benign browser notice raised when a resize callback schedules
  //         another resize within the same frame. No application impact, fires on all modern browsers.
  // #8424e: AuthInterceptor logs "AuthInterceptor: TenantUuid is null or undefined." when a tenant-scoped
  //         request is attempted before the working context is ready. The interceptor already cancels the
  //         request (status -1), so this is a defensive guard hit, not an error — keep it queryable as warn.
  // #8569:  angular-oauth2-oidc loadUserProfile() throws a TypeError (startsWith on null) when the userinfo
  //         response reaches the browser without a Content-Type header (stripped by a proxy/antivirus/
  //         extension on the user side). Upstream bug, still present in v22 (issue #828), non-actionable on
  //         our side; only the id_token claims merge is skipped. Scoped to the userinfo message so an
  //         application null-dereference on startsWith stays at error level.
  private _downgradeKnownFalsePositives(event: LogsEvent): boolean {
    if (event.status === 'error') {
      if (event.http?.status_code === 0) {
        event.status = 'warn';
      }

      // Aborted HttpClient requests (cancelled on navigation/switchMap) surface as AbortError
      // since the move to provideHttpClient(withFetch()). They are expected and benign.
      if (event.message?.includes('AbortError') || event.message?.includes('The user aborted a request')) {
        event.status = 'warn';
      }

      if (event.message?.includes('Script error.')) {
        event.status = 'warn';
      }

      if (event.message?.includes('error loading') && /"status"\s*:\s*0\b/.test(event.message)) {
        event.status = 'warn';
      }

      if (event.message?.includes('error loading user info') && event.message.includes('startsWith')) {
        event.status = 'warn';
      }

      if (event.message?.includes('Token has expired') || event.message?.includes('Error validating tokens')) {
        event.status = 'warn';
      }

      if (event.message?.includes('issuedAtMSec') && event.message?.includes('expiresAtMSec')) {
        event.status = 'warn';
      }

      if (event.message?.includes('wrong state/nonce')) {
        event.status = 'warn';
      }

      if (event.message?.includes('can not transition to') && event.message.includes('expecting state')) {
        event.status = 'warn';
      }

      if (event.message?.includes('Error getting token') && /"status"\s*:\s*0\b/.test(event.message)) {
        event.status = 'warn';
      }

      if (event.message?.includes('runInitialLoginSequence') && /"status"\s*:\s*0\b/.test(event.message)) {
        event.status = 'warn';
      }

      if (event.message?.includes('Wrong nonce')) {
        event.status = 'warn';
      }

      if (event.message?.includes('ResizeObserver loop')) {
        event.status = 'warn';
      }

      if (event.message?.includes('AuthInterceptor: TenantUuid')) {
        event.status = 'warn';
      }
    }

    return true;
  }

  private _flushQueue(): void {
    if (!this._datadogLogs) {
      return;
    }
    for (const entry of this._queuedLogs) {
      this._datadogLogs.logger[entry.level](entry.msg);
    }
    this._queuedLogs = [];
  }

  private _updateDatadogContext(state: { userUuid: string | null; laboratoryCode: string | null; customerCode: string | null }): void {
    if (!this.allowLogFlag || !this._datadogLogs) {
      return;
    }

    const currentContext = this._datadogLogs.getGlobalContext();
    const newContext = { ...currentContext } as Record<string, Record<string, unknown>>;

    if (!newContext['context']) {
      newContext['context'] = {};
    }

    if (state.userUuid) {
      newContext['context']['user_uuid'] = state.userUuid;
    } else {
      delete newContext['context']['user_uuid'];
    }

    if (state.laboratoryCode) {
      newContext['context']['laboratory_code'] = state.laboratoryCode;
    } else {
      delete newContext['context']['laboratory_code'];
    }

    if (state.customerCode) {
      newContext['context']['customer_code'] = state.customerCode;
    } else {
      delete newContext['context']['customer_code'];
    }

    this._datadogLogs.setGlobalContext(newContext);
  }
}
